import type { AxiosError } from "axios";
import type { MetaChipTone } from "../../../../../components/UIToolkit/Badges/MetaChip";
import type { ClipboardOnboardingStatus, ClipboardProposalReason, ClipboardRoleDisposition } from "./clipboardOnboardingTypes";

/**
 * Every non-`ok` status a Clipboard onboarding call can answer with, mapped to the sentence that tells
 * support what to DO about it. The point of the discriminator is that an empty facility list gets
 * explained rather than just rendered empty — so there is no generic fallback here on purpose.
 */
export const CLIPBOARD_STATUS_MESSAGE_KEY: Record<Exclude<ClipboardOnboardingStatus, "ok">, string> = {
	no_facilities: "department.config.clipboard.wizard.status.noFacilities",
	credentials_missing: "department.config.clipboard.wizard.status.credentialsMissing",
	facility_not_managed: "department.config.clipboard.wizard.status.facilityNotManaged",
	clipboard_error: "department.config.clipboard.wizard.status.clipboardError",
};

/** Why the proposal landed where it did — shown inline so an operator can disagree with a specific reason. */
export const CLIPBOARD_PROPOSAL_REASON_KEY: Record<ClipboardProposalReason, string> = {
	exact_sub_role: "department.config.clipboard.wizard.roles.reason.exactSubRole",
	cert_override_chain: "department.config.clipboard.wizard.roles.reason.certOverrideChain",
	no_enabled_job: "department.config.clipboard.wizard.roles.reason.noEnabledJob",
	already_ignored: "department.config.clipboard.wizard.roles.reason.alreadyIgnored",
	already_mapped: "department.config.clipboard.wizard.roles.reason.alreadyMapped",
	no_confident_proposal: "department.config.clipboard.wizard.roles.reason.noConfidentProposal",
};

export const CLIPBOARD_DISPOSITION_LABEL_KEY: Record<ClipboardRoleDisposition, string> = {
	allowed: "department.config.clipboard.wizard.roles.disposition.allowed",
	ignored: "department.config.clipboard.wizard.roles.disposition.ignored",
	unmapped: "department.config.clipboard.wizard.roles.disposition.unmapped",
};

/**
 * `unmapped` is the failure this whole wizard exists to remove, so it wears the error tone rather than a
 * warning — a dropped shift is not a caveat. `ignored` is a deliberate choice, so it is neutral, not a problem.
 */
export const CLIPBOARD_DISPOSITION_TONE: Record<ClipboardRoleDisposition, MetaChipTone> = {
	allowed: "success",
	ignored: "neutral",
	unmapped: "error",
};

/** An already-translatable message: the i18n key plus whatever the backend attached to interpolate into it. */
export type ClipboardErrorMessage = { key: string; params: Record<string, unknown> };

/**
 * The message to show for a failed Clipboard onboarding request.
 *
 * Prefers the backend's `UIVisibleError` payload — that is how "already mapped to <department>" and
 * "this login does not manage that facility" reach the operator with the specifics. `details` is passed
 * straight through as interpolation values, so `clipboard.facility.already.mapped` can name the owning
 * department instead of saying "already mapped" and leaving support to go find it.
 *
 * Everything else collapses to one generic sentence rather than surfacing a raw exception: the
 * credential calls carry a password, and their upstream errors are not safe to render verbatim.
 */
export function clipboardErrorMessage(error: unknown): ClipboardErrorMessage {
	const detail = (error as AxiosError<{ detail?: { message?: string; error_type?: string; details?: Record<string, unknown> } | string }>)?.response?.data
		?.detail;
	if (detail && typeof detail === "object" && detail.error_type === "UIVisibleError" && detail.message) {
		return { key: detail.message, params: detail.details ?? {} };
	}
	// Anything that is not a UIVisibleError collapses to one generic sentence. A raw `detail` string is
	// deliberately NOT rendered: FastAPI's own 401/403/422 bodies are internal prose ("Only support users
	// can change clipboard_facility_id"), and these calls carry a password, so their upstream errors are
	// not safe to put in front of a user verbatim.
	return { key: "department.config.clipboard.wizard.status.requestFailed", params: {} };
}

/** True when the failure is Clipboard itself being unreachable — a retry is the right offer, not a dead end. */
export function isClipboardUnreachable(error: unknown): boolean {
	return clipboardErrorMessage(error).key === "clipboard.roles.fetch.failed";
}
